import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import { SearchParams } from 'api/ResourceAPI';
import useApi from 'hooks/useApi';
import { useCallback, useRef } from 'react';
import { injectTotalWorkunitsBLAction, injectFilterTotalWorkunitsBLAction } from 'store/actions/changeBaselineActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { SelectedFiltersList } from 'types/models/SelectedFiltersList';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import Order from 'types/entities/Order';

export default function useTotalWorkunitService() {
  const hasDispatchedFilterRef = useRef(false);
  const { app, filter } = useSelector((state) => state);
  const { baseLineOrder, sortBLtotalWorkunitModel, deliveryManagers } = useSelector((state) => state.changeBaseline);
  const { makeCall } = useApi();
  const { dispatch } = useAppState();

  const fetchOrderWorkunits = useCallback(
    async (searchParams: SearchParams, setIsLoading: any) => {
      try {
        setIsLoading(true);
        const { datas, totalPages, totalItems, currentPage } = await makeCall(
          ChangeBaseLineAPIServices.fetchBaseLineAffectationMissions(Number(baseLineOrder?.id), 3, searchParams),
          'Unable to retrieve order workunits',
          'totalworkunits'
        );
        datas?.forEach((element) => {
          if (element?.delivery_manager_fullname) {
            const matchingId = deliveryManagers?.find((user) => {
              const fullNameFromList = `${user.last_name} ${user.first_name}`.toLowerCase().trim();
              const fullNameFromElement = element?.delivery_manager_fullname?.toLowerCase().trim();
              return fullNameFromList === fullNameFromElement;
            });
            if (matchingId) {
              element.delivery_manager = matchingId;
              element.delivery_manager_id = matchingId?.id;
            }
          }
        });
        dispatch(
          injectTotalWorkunitsBLAction({
            datas,
            totalItems,
            totalPages,
            currentPage,
          })
        );

        if (!hasDispatchedFilterRef.current) {
          dispatch(
            injectFilterTotalWorkunitsBLAction({
              datas,
            })
          );
          hasDispatchedFilterRef.current = true;
        }
        setIsLoading(false);
        return { datas, totalPages, totalItems, currentPage };
      } catch (error) {
        setIsLoading(false);
        console.error('Failed to fetch order workunits:', error);
        // Return null to signal an error/no data fetched
        return null;
      }
    },
    [baseLineOrder?.id, makeCall] // Ensure all dependencies are correct
  );

  const fetchParamsBLFilterDeliverable = useCallback(
    async (source_id: Order['id'], baseline_version: number) => {
      try {
        const filterParams = await makeCall(
          ChangeBaseLineAPIServices.fetchParamsBLFilter(
            Number(source_id),
            Number(baseLineOrder?.id),
            Number(baseline_version)
          ),
          'Unable to retrieve ',
          ''
        );
        return filterParams;
      } catch (error) {
        console.error('Failed to fetch ', error);
        // Return null to signal an error/no data fetched
        return null;
      }
    },
    [baseLineOrder?.id, makeCall] // Ensure all dependencies are correct
  );

  const handleBLTotalworkunitSearchParams = useCallback(
    async (pageSize: number, currentPage: number, searchParam: SearchParams, setIsLoading: any) => {
      if (!app.customer) {
        return;
      }
      // TODO add sort param
      let searchParams: SearchParams = {
        customer_id: app.customer.id,
        // is_archived: false,
        join: [
          'scope',
          'workunit',
          // 'mission-frequency',
          // 'mission-status',
          // 'mission-comments',
          // 'mission-comments.author',
          'mission-advancement',
          'consultant',
          // 'reviewer',
          // 'validator',
          'client',
          // 'order-scope',
          'delivery_manager',
        ],
        size: pageSize,
        page: currentPage,
        ...searchParam,
      };

      if (sortBLtotalWorkunitModel.length > 0) {
        const { field, sort } = sortBLtotalWorkunitModel[0];

        let column_value = '';
        if (field === 'delivery_manager') {
          column_value = 'delivery_manager_fullname';
        } else if (field === 'comments') {
          column_value = 'mission-comments';
        } else if (field === 'workload') {
          column_value = 'charge';
        } else if (field === 'complexity') {
          column_value = 'complexity_name';
        } else if (field === 'amount') {
          column_value = 'price';
        } else if (field === 'deliverableDate') {
          column_value = 'delivery_date';
        } else if (field === 'forcastedDate') {
          column_value = 'forecast_date';
        } else if (field === 'scope') {
          column_value = 'scope_id';
        } else if (field === 'consultant') {
          column_value = 'consultant_fullname';
        } else if (field === 'client') {
          column_value = 'client_fullname';
        } else if (field === 'delivery_manager') {
          column_value = 'delivery_manager_fullname';
        } else if (
          field === 'submitted' ||
          field === 'dm_validation' ||
          field === 'client_acceptance' ||
          field === 'actions'
        ) {
          column_value = 'mission_advancement_id';
        } else if (field === 'tags') {
          column_value = 'label';
        } else if (field === 'purchase_order') {
          column_value = 'purchase_order_id';
        } else {
          column_value = field;
        }

        const sortParam = `${column_value}.${sort}`;
        searchParams = {
          ...searchParams,
          sorted: sortParam,
        };
      }
      await fetchOrderWorkunits(searchParams, setIsLoading);
    },
    [sortBLtotalWorkunitModel]
  );

  const deleteMissionIdOptionClickable = (searchParams: SearchParams) => {
    delete filter.selected?.mission_advancement_id;
    delete searchParams?.mission_advancement_id;
  };

  return {
    handleBLTotalworkunitSearchParams,
    fetchOrderWorkunits,
    fetchParamsBLFilterDeliverable,
  };
}
